import React, { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { currentWeek, addWeeks } from "@/lib/week";
import { dagVan, pasDagToe } from "./overlegRegels";

const DAGEN = ["maandag", "dinsdag", "woensdag", "donderdag", "vrijdag"];
const input = "px-2.5 py-1.5 rounded-md border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-600";
const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 30);

// v19-B4: Instellingen → Afdelingen. Naam, actief, overlegdag "geldt vanaf", vaste vragen. Opslaan met baseVersion (409 bij conflict).
export function AfdelingenEditor({ meta, onSaved }) {
  const [rows, setRows] = useState([]);
  const [vanaf, setVanaf] = useState({});
  const [bezig, setBezig] = useState(false);
  const versie = meta.configVersions?.afdelingen;
  useEffect(() => { setRows((meta.afdelingenCfg || []).map((a) => ({ ...a, vragen: [...(a.vragen || [])], _bestaat: true, _server: a.overlegDag || [] }))); setVanaf({}); }, [versie]); // eslint-disable-line react-hooks/exhaustive-deps
  const nu = currentWeek();
  const upd = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const weekVoor = (wanneer) => (wanneer === "volgende" ? addWeeks(nu, 1) : nu);
  // v19-R1 R2: altijd vanaf de serverregels opbouwen, zodat "dag dan vanaf" en "vanaf dan dag" hetzelfde opslaan.
  const zetDag = (i, dag, wanneer) => upd(i, { _dag: dag, overlegDag: pasDagToe(rows[i]._server, dag, weekVoor(wanneer)) });
  const kiesVanaf = (i, sleutel, wanneer) => {
    setVanaf((v) => ({ ...v, [sleutel]: wanneer }));
    if (rows[i]._dag !== undefined) upd(i, { overlegDag: pasDagToe(rows[i]._server, rows[i]._dag, weekVoor(wanneer)) });
  };
  const nieuw = () => setRows((rs) => [...rs, { id: "", naam: "", actief: true, overlegDag: [{ vanafWeek: nu, dag: 0 }], vragen: [], _server: [], _dag: 0 }]);
  const opslaan = async () => {
    const lijst = rows.map((r) => ({ id: r._bestaat ? r.id : slug(r.naam), naam: r.naam, actief: r.actief, overlegDag: r.overlegDag,
      vragen: r.vragen.filter((v) => v.text.trim()) }));
    if (lijst.some((r) => !r.id || !r.naam.trim())) { toast.error("Geef elke afdeling een naam."); return; }
    setBezig(true);
    try { await api.put("/config/afdelingen", { afdelingen: lijst, baseVersion: versie }); toast.success("Afdelingen opgeslagen"); onSaved(); }
    catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
      if (e?.response?.status === 409) onSaved();
    } finally { setBezig(false); }
  };
  return (
    <div data-testid="afdelingen-editor" className="bg-white border border-zinc-200 rounded-[10px] p-4 md:p-5 space-y-4">
      <div>
        <h3 className="font-heading font-black text-lg text-zinc-900">Afdelingen</h3>
        <p className="text-sm text-zinc-500">Een overlegdag geldt vanaf deze of volgende week; voorbije weken blijven zoals ze waren. Een afdeling verwijder je niet, je zet hem op niet actief.</p>
      </div>
      {rows.map((r, i) => {
        const k = r.id || `nieuw-${i}`;
        const dagNu = r._dag !== undefined ? r._dag : dagVan(r._server, weekVoor(vanaf[r.id || i]));
        return (
          <div key={k} data-testid={`afd-editor-${k}`} className="border border-zinc-200 rounded-md p-3 space-y-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <input data-testid={`afd-editor-naam-${k}`} className={`${input} flex-1 min-w-[180px]`} value={r.naam} placeholder="Naam van de afdeling" onChange={(e) => upd(i, { naam: e.target.value })} />
              {r._bestaat && <span className="font-mono text-[11px] text-zinc-400">{r.id}</span>}
              <label className="flex items-center gap-1.5 text-sm text-zinc-700">
                <input type="checkbox" data-testid={`afd-editor-actief-${k}`} checked={r.actief !== false} onChange={(e) => upd(i, { actief: e.target.checked })} className="h-4 w-4 accent-blue-600" /> Actief
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-700">
              Overleg op
              <select data-testid={`afd-editor-dag-${k}`} className={input} value={dagNu} onChange={(e) => zetDag(i, Number(e.target.value), vanaf[r.id || i] || "deze")}>
                {DAGEN.map((d, n) => <option key={d} value={n}>{d}</option>)}
              </select>
              geldt vanaf
              <select data-testid={`afd-editor-vanaf-${k}`} className={input} value={vanaf[r.id || i] || "deze"} onChange={(e) => kiesVanaf(i, r.id || i, e.target.value)}>
                <option value="deze">deze week</option><option value="volgende">volgende week</option>
              </select>
            </div>
            {(r.overlegDag || []).filter((x) => x.vanafWeek > nu).map((x) => (
              <p key={x.vanafWeek} data-testid={`afd-editor-gepland-${k}`} className="text-xs text-zinc-500">Gepland: vanaf week {Number(x.vanafWeek.slice(-2))} op {DAGEN[x.dag]}.</p>
            ))}
            <div className="space-y-1.5">
              <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">Vaste vragen</div>
              {r.vragen.map((v, j) => (
                <div key={v.id} className="flex gap-2">
                  <input data-testid={`afd-editor-vraag-${k}-${j}`} className={`${input} flex-1`} value={v.text} onChange={(e) => upd(i, { vragen: r.vragen.map((x, n) => (n === j ? { ...x, text: e.target.value } : x)) })} />
                  <button type="button" aria-label="Vraag weghalen" data-testid={`afd-editor-vraag-weg-${k}-${j}`} onClick={() => upd(i, { vragen: r.vragen.filter((_, n) => n !== j) })} className="p-2 text-zinc-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <button type="button" data-testid={`afd-editor-vraag-plus-${k}`} onClick={() => upd(i, { vragen: [...r.vragen, { id: `${r.id || slug(r.naam) || "afd"}-v${Date.now().toString(36)}`, text: "" }] })}
                className="flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900"><Plus className="h-3.5 w-3.5" /> Vraag</button>
            </div>
          </div>
        );
      })}
      <div className="flex flex-wrap gap-2">
        <button type="button" data-testid="afd-editor-nieuw" onClick={nieuw} className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-300 text-sm font-semibold text-zinc-700 hover:bg-zinc-50"><Plus className="h-4 w-4" /> Afdeling</button>
        <button type="button" data-testid="afd-editor-opslaan" disabled={bezig} onClick={opslaan} className="px-4 py-2 rounded-md bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-60">Opslaan</button>
      </div>
    </div>
  );
}
